import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import format from 'string-template';

import { ReactComponent as CheckIcon } from '../../../../../assets/icons/selected-icon.svg';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useToast from '../../../../hooks/useToast';
import FirebaseContext from '../../../../../context/FirebaseContext';
import LeadsContext from '../../../../context/LeadsContext';
import UserContext from '../../../../../context/UserContext';
import Lead from '../../../../Model/Lead';
import LoadingOverlay from '../../../LoadingOverlay';
import { claimLead } from '../../utils';
import { claimLeadWithHistory, getClaimRejectionText } from '../NewLeadNotificationModal/utils';
import texts from './texts.json';
import {
  StyledAlert,
  StyledButton,
  StyledAlertIcon,
} from './styles';

const ClaimLeadButton = ({
  onLeadClaim,
}) => {
  const [isClaiming, setIsClaiming] = useState(false);
  const { showToast } = useToast();
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { userDoc } = useContext(UserContext);
  const { unclaimedLeadsCollection, loadingLeads } = useContext(LeadsContext);
  const availableLeadsCount = unclaimedLeadsCollection?.docs?.length || 0;
  const isComponentMountedRef = useComponentMounted();

  const isButtonDisabled = isClaiming || availableLeadsCount === 0 || loadingLeads;
  const isLeadsAvailable = availableLeadsCount > 0;

  const handleClaimLead = async () => {
    if (isButtonDisabled) return;

    setIsClaiming(true);
    try {
      const result = await claimLead(remote);

      if (!result.success || result.error) {
        throw new Error(result.error);
      }

      const claimedLead = await Lead.getLeadById(result.leadId);
      const claimResult = await claimLeadWithHistory(claimedLead, userDoc);

      if (!claimResult.success) {
        if (isComponentMountedRef.current) {
          showToast(getClaimRejectionText(claimResult.reason, texts), { error: true });
        }
        return;
      }

      if (isComponentMountedRef.current) {
        showToast(texts.claimSuccess);
        onLeadClaim(claimedLead);
      }
    } catch (error) {
      showToast(format(texts.claimError, { error: error.message }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsClaiming(false);
      }
    }
  };

  return (
    <>
      <StyledAlert
        severity={isLeadsAvailable ? 'success' : 'info'}
        iconMapping={{ success: <StyledAlertIcon /> }}
        $isLeadsAvailable={isLeadsAvailable}
      >
        {isLeadsAvailable
          ? format(texts.availableToClaimBadge, { count: availableLeadsCount })
          : texts.noLeadsAvailable}
        <StyledButton
          icon={<CheckIcon />}
          size="medium"
          disabled={isButtonDisabled}
          onClick={handleClaimLead}
        >
          {texts.claimNow}
        </StyledButton>
      </StyledAlert>
      <LoadingOverlay isLoading={isClaiming} />
    </>
  );
};

ClaimLeadButton.propTypes = {
  onLeadClaim: PropTypes.func.isRequired,
};

export default observer(ClaimLeadButton);
